/**
 * Promise Executor Monitoring
 *
 * Simple, configurable tracing for promise executor.
 * Integrates with PerformanceMonitoringService for ES logging.
 *
 * Features:
 * - Batches metrics and pushes in one go (debounced)
 * - Only pushes promises that exceed duration threshold (default 1s)
 *
 * Modes:
 * - OFF: No tracing
 * - ERROR_ONLY: Only log failures (default)
 * - SAMPLING: Log % of promises
 * - ALL: Log everything
 *
 *
 * // Direct access (if already loaded)
 * // Example: Log all promises (not just errors) and no duration threshold
window.__monitoringConfig = await import('/src/util/promiseExecutorMonitoring.ts')
window.__monitoringConfig.setTracingConfig({
  mode: 'ALL',        // Log all promises (not just errors)
  minDurationMs: 0    // No duration threshold
})
 */

import PerformanceMonitoringService from '../../services/PerformanceMonitoringService'
import store from '../../store'
import { getLatestLCP, getLatestINP, getLatestCLS, getNetworkData } from '../webVitalsService'

// ============================================================================
// Types & Config
// ============================================================================

export type TracingMode = 'OFF' | 'ERROR_ONLY' | 'SAMPLING' | 'ALL'

export interface PromiseMetrics {
  context: string
  type: 'critical' | 'degradable'
  duration: number
  durationUnit: 'ms'
  status: 'success' | 'error'
  promises: string[]
  failedPromises?: string[]
  error?: string
  timestamp?: string
  sessionId?: string
  traceId?: string
  userId?: string
  // Web vitals snapshot at time of metric capture
  lcp?: number
  inp?: number
  cls?: number
  effectiveNetworkType?: string
  networkStrength?: string
  browser?: string
  os?: string
}

export interface IndividualPromiseMetrics {
  context: string
  type: 'critical' | 'degradable'
  promiseName: string
  duration: number
  durationUnit: 'ms'
  status: 'success' | 'error'
  error?: string
  timestamp?: string
  sessionId?: string
  traceId?: string
  userId?: string
  // Web vitals snapshot at time of metric capture
  lcp?: number
  inp?: number
  cls?: number
  effectiveNetworkType?: string
  networkStrength?: string
  browser?: string
  os?: string
}

type AnyMetrics = PromiseMetrics | IndividualPromiseMetrics

interface BatchPayload {
  metrics: AnyMetrics[]
  sessionId?: string
  userId?: string
  timestamp: string
}

type BatchPusher = (payload: BatchPayload) => void

interface Config {
  mode: TracingMode
  samplingRate: number
  batchPusher: BatchPusher | null
  /** Minimum duration in ms to push metrics (default: 1000ms) */
  minDurationMs: number
  /** Debounce delay in ms before pushing batch (default: 100ms) */
  batchDelayMs: number
}

/** Get session ID from sessionStorage */
const getSessionId = (): string | undefined => {
  try {
    return sessionStorage.getItem('sessionId') || undefined
  } catch {
    return undefined
  }
}

/** Get user ID from store */
const getUserId = (): string | undefined => {
  try {
    return store.state.user?.user?.id
  } catch {
    return undefined
  }
}

/** Generate a unique trace ID for correlating promises within a batch */
export const generateTraceId = (): string => `pr-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

// ============================================================================
// Batch Collection
// ============================================================================

/** Collected metrics waiting to be pushed */
let metricsBatch: AnyMetrics[] = []

/** Debounce timer for batch push */
let batchTimer: ReturnType<typeof setTimeout> | null = null

/** Push collected batch to backend */
const flushBatch = () => {
  if (metricsBatch.length === 0) return

  const payload: BatchPayload = {
    metrics: metricsBatch,
    sessionId: getSessionId(),
    userId: getUserId(),
    timestamp: new Date().toISOString(),
  }

  // Clear batch before pushing (in case push is async)
  metricsBatch = []

  if (config.batchPusher) {
    try {
      config.batchPusher(payload)
    } catch {
      // Silently ignore
    }
  }
}

/** Add metrics to batch and schedule push */
const addToBatch = (metrics: AnyMetrics) => {
  // Capture web vitals snapshot at time of metric recording
  const lcp = getLatestLCP()
  const inp = getLatestINP()
  const cls = getLatestCLS()
  const network = getNetworkData()

  metricsBatch.push({
    ...metrics,
    timestamp: new Date().toISOString(),
    // Web vitals snapshot
    lcp: lcp?.lcp,
    inp: inp?.inp,
    cls: cls?.cumulativeValue,
    effectiveNetworkType: network.effectiveType,
    networkStrength: network.strength,
    browser: network.browser,
    os: network.os,
  })

  // Debounce: reset timer on each add, push after delay
  if (batchTimer) {
    clearTimeout(batchTimer)
  }
  batchTimer = setTimeout(flushBatch, config.batchDelayMs)
}

/** Default batch pusher using PerformanceMonitoringService */
const defaultBatchPusher: BatchPusher = payload => {
  PerformanceMonitoringService.logPromiseExecutorMetrics(payload)
}

const config: Config = {
  mode: 'SAMPLING',
  samplingRate: 0.2,
  batchPusher: defaultBatchPusher,
  minDurationMs: 2000,
  batchDelayMs: 2000, // 2 second debounce
}

// ============================================================================
// Public API
// ============================================================================

/** Update tracing config */
export const setTracingConfig = (updates: Partial<Config>) => {
  Object.assign(config, updates)
}

/** Get current config */
export const getTracingConfig = () => ({ ...config })

/** Force flush any pending metrics (useful for page unload) */
export const flushMetrics = () => {
  if (batchTimer) {
    clearTimeout(batchTimer)
    batchTimer = null
  }
  flushBatch()
}

/** Build metrics object */
export const buildMetrics = (
  context: string,
  type: 'critical' | 'degradable',
  promises: string[],
  startTime: number,
  status: 'success' | 'error',
  failedPromises?: string[],
  error?: Error,
  traceId?: string
): PromiseMetrics => ({
  context,
  type,
  duration: Math.round(performance.now() - startTime),
  durationUnit: 'ms',
  status,
  promises,
  failedPromises,
  error: error?.message,
  traceId,
})

/** Build individual promise metrics object */
export const buildIndividualMetrics = (
  context: string,
  type: 'critical' | 'degradable',
  promiseName: string,
  startTime: number,
  status: 'success' | 'error',
  error?: Error,
  traceId?: string
): IndividualPromiseMetrics => ({
  context,
  type,
  promiseName,
  duration: Math.round(performance.now() - startTime),
  durationUnit: 'ms',
  status,
  error: error?.message,
  traceId,
})

/** Notify monitoring of completed promise (batch or individual) */
export const notifyPromiseComplete = (metrics: AnyMetrics) => {
  if (!shouldTrace(metrics.status, metrics.duration)) return

  addToBatch(metrics)
}

// ============================================================================
// Internal
// ============================================================================

const shouldTrace = (status: 'success' | 'error', duration: number): boolean => {
  // Only push metrics if duration exceeds threshold (default 1 second)
  if (duration >= config.minDurationMs) return true

  switch (config.mode) {
    case 'OFF':
      return false
    case 'ERROR_ONLY':
      return status === 'error'
    case 'SAMPLING':
      return status === 'error' || Math.random() < config.samplingRate
    case 'ALL':
      return true
    default:
      return false
  }
}
